import { useCallback, useState } from 'react';

import { GRAY_50_HEX } from 'shared';

import FilterDropdown from './FilterDropdown';
import { HEADER_INPUT_SIZE } from './constants';
import { Column, FilterState, Row, SearchResult, SetFilter } from './types';
import Layout from '../Layout';
import Text from '../Text';
import TextInput from '../inputs/TextInput';

interface TableControlsHeaderProps<T extends Row> {
  disableSearch?: boolean;
  columns: Column<T>[];
  removeFilter: SetFilter;
  rows: T[];
  filterState: FilterState[];
  sortState: any[];
  setFilterState: (filters: FilterState[]) => void;
}

const TableControlsHeader = <T extends Row>({
  disableSearch = false,
  columns,
  removeFilter,
  filterState,
  setFilterState
}: TableControlsHeaderProps<T>) => {
  const columnsWithTextSearch = columns.filter(column => column.filters?.textSearch?.enabled);
  // existingSearchValue looks for a filterState with a key that matches a columnWithTextSearch.key
  const existingSearchValue = filterState.find(filter =>
    columnsWithTextSearch.some(column => column.key === filter.key)
  );

  const columnWithKey = columns.find(column => column.key === existingSearchValue?.key);
  const existingSearchValueText = columnWithKey?.filters?.textSearch?.keys?.find(
    key => key.id === existingSearchValue?.equals
  )?.value;

  const [searchValue, setSearchValue] = useState(existingSearchValueText || '');
  const [searchResults, setSearchResults] = useState<
    Array<{ value: string; column: string; imgOrIcon?: React.ReactNode; id: number }>
  >([]);
  const [showResults, setShowResults] = useState(false);

  const columnNamesWithTextSearch = columns
    .filter(column => column.filters?.textSearch?.enabled)
    .map(column => column.header.toLowerCase());

  const allButLast = columnNamesWithTextSearch.slice(0, -1);
  const last = columnNamesWithTextSearch[columnNamesWithTextSearch.length - 1];

  // Get all unique values from enabled columns
  const getUniqueValues = useCallback(() => {
    const values = new Set<{ value: string; column: string; imgOrIcon?: React.ReactNode; id: number }>();

    columns.forEach(column => {
      if (column.filters?.textSearch?.enabled && column.filters.textSearch.keys) {
        // Use the predefined keys for searching
        column.filters.textSearch.keys.forEach((key: SearchResult) => {
          if (key.value) {
            values.add({ value: key.value, column: column.header, imgOrIcon: key.imgOrIcon, id: key.id });
          }
        });
      }
    });

    return Array.from(values);
  }, [columns]);

  // Handle search input changes
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (value.length > 0) {
      const matches = getUniqueValues().filter(item => item.value.toLowerCase().includes(value.toLowerCase()));
      setSearchResults(matches);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  // get columns with one of filter
  const getColumnsWithOneOfFilter = () => {
    return columns.filter(column => column.filters?.oneOf?.options?.length && column.filters.oneOf.options.length > 0);
  };

  // get column with quick filter
  const columnWithQuickFilter = columns.find(
    column => column.filters?.quickFilter?.options?.length && column.filters.quickFilter.options.length > 0
  );

  const columnFilterState = filterState.find(filter => filter.key === columnWithQuickFilter?.key) || {
    key: (columnWithQuickFilter?.key as string) || ''
  };
  const quickFilter = columnWithQuickFilter?.filters?.quickFilter;

  // Get the search input column
  const getSearchInputColumn = () => {
    const textSearchColumnSelected = filterState.find(filter => filter.equals);

    return textSearchColumnSelected?.key;
  };

  // Handle result selection
  const handleResultSelect = (result: { value: string; column: string; imgOrIcon?: React.ReactNode; id: number }) => {
    const column = columns.find(c => c.header === result.column);
    if (column) {
      // Remove filters for all columns that have text search enabled
      const updatedFilterState = filterState.filter(filter => {
        const columnWithFilter = columns.find(c => c.key === filter.key);
        return !columnWithFilter?.filters?.textSearch?.enabled;
      });

      // Add the new filter
      updatedFilterState.push({
        key: String(column.key),
        equals: result.id
      });

      setFilterState(updatedFilterState);
      setSearchValue(result.value);
    }
    setShowResults(false);
  };

  return (
    <Layout
      paddingX={24}
      paddingY={12}
      __style={{ borderBottom: `2px solid ${GRAY_50_HEX}` }}
      direction="row"
      justify="space-between"
      gap={12}
      position="sticky"
      top={0}
      background="white"
      zIndex={1000}
    >
      <Layout direction="row" gap={8}>
        {!disableSearch && (
          <Layout width={275} position="relative">
            <TextInput
              icon="search"
              label={`Search by ${columnNamesWithTextSearch.length > 1 ? `${allButLast.join(', ')} or ${last}` : last}`}
              hideLabel={true}
              size={HEADER_INPUT_SIZE}
              placeholder={`Search by ${
                columnNamesWithTextSearch.length > 1 ? `${allButLast.join(', ')} or ${last}` : last
              }`}
              value={searchValue}
              onChange={({ value }) => handleSearchChange(value)}
              onClear={() => {
                const searchColumn = getSearchInputColumn();
                if (searchColumn) {
                  // Use setFilter to remove the filter for this column
                  removeFilter({
                    equals: '',
                    oneOf: [],
                    startDate: undefined,
                    endDate: undefined,
                    minValue: undefined,
                    maxValue: undefined
                  });
                }
                setSearchValue('');
                setShowResults(false);
              }}
            />
            {showResults && searchResults.length > 0 && (
              <Layout
                position="absolute"
                borderRadius={8}
                top="100%"
                width={275}
                left={0}
                right={0}
                marginTop={4}
                color="white"
                boxShadow="2px 0 22px 0 rgba(34.1176, 28.6275, 42.7451, 0.1200)"
                minHeight={160}
                maxHeight={160}
                overflow="auto"
                zIndex={1000}
                paddingY={8}
              >
                {searchResults.map((result, index) => (
                  <button
                    key={`${result.value}-${index}`}
                    className="hover-bg-color-gray-50"
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      height: '36px',
                      width: '100%'
                    }}
                    onClick={() => handleResultSelect(result)}
                  >
                    <Layout direction="row" gap={12} align="center">
                      {result.imgOrIcon}
                      <Text size="body-xs" __style={{ width: 'fit-content' }} align="left" ellipsis>
                        {result.value}
                      </Text>
                    </Layout>
                  </button>
                ))}
              </Layout>
            )}
          </Layout>
        )}

        {getColumnsWithOneOfFilter().map(column => {
          const columnFilterState = filterState.find(filter => filter.key === column.key) || {
            key: column.key as string
          };
          const defaultOption = column.filters?.oneOf?.defaultOption;

          if (!defaultOption) {
            return null;
          }

          return (
            <FilterDropdown
              key={String(column.key)}
              filterState={columnFilterState}
              setFilterState={newFilterState => {
                const updatedFilterState = filterState.filter(f => f.key !== column.key);

                const defaultIsAny = defaultOption.any;
                if (newFilterState.oneOf?.[0] !== defaultOption.anyFilterOption?.value || !defaultIsAny) {
                  updatedFilterState.push(newFilterState);
                }
                setFilterState(updatedFilterState);
              }}
              options={column.filters?.oneOf?.options || []}
              defaultOption={defaultOption}
              column={column}
            />
          );
        })}
      </Layout>
      {quickFilter && (
        <Layout>
          <Layout direction="row" height={32} padding={2} color="gray-25" borderRadius={8}>
            {quickFilter.options.map(option => {
              const isSelected =
                option.value === 'All' ? !columnFilterState.oneOf : columnFilterState.oneOf?.[0] === option.value;
              return (
                <button
                  key={String(option.value)}
                  className={`${isSelected ? 'bg-color-white' : ''}`}
                  style={{
                    padding: '6px 16px',
                    cursor: 'pointer',
                    height: '28px',
                    borderRadius: '6px'
                  }}
                  onClick={() => {
                    const updatedFilterState = filterState.filter(f => f.key !== columnWithQuickFilter.key);
                    if (option.value !== 'All') {
                      updatedFilterState.push({
                        key: String(columnWithQuickFilter.key),
                        oneOf: [String(option.value)]
                      });
                    }
                    setFilterState(updatedFilterState);
                  }}
                >
                  <Text size="body-xs" semibold>
                    {option.label}
                  </Text>
                </button>
              );
            })}
          </Layout>
        </Layout>
      )}
    </Layout>
  );
};

export default TableControlsHeader;
